import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';

import { useChat } from '../../context/ChatContext';
import Spinner from '../ui/Spinner';
import ChatMessageBubble from './ChatMessageBubble';

// Scrollable transcript + the daily-limit "reached your limit" state
// (chat_bot.md §3.4 — inline in the bot's voice, not a Toast).
export default function ChatMessageList() {
  const { messages, sending, streamingContent, toolCallActive, initializing, dailyLimitReached } = useChat();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, sending, streamingContent, toolCallActive, dailyLimitReached]);

  if (initializing) {
    return (
      <div className="ss-chat-empty">
        <Spinner label="Loading conversation" />
      </div>
    );
  }

  return (
    <div className="ss-chat-messages">
      {messages.length === 0 && !dailyLimitReached && (
        <div className="ss-chat-empty">
          <MessageCircle size={32} aria-hidden="true" />
          <p>Ask about a product, an order, or your cart — I'm happy to help.</p>
        </div>
      )}

      {messages.map((m) => (
        <ChatMessageBubble key={m._id} message={m} />
      ))}

      {sending && (streamingContent || toolCallActive ? (
        // Live streaming reply (Phase 4 SSE deltas) — reuses ChatMessageBubble's
        // existing "looking that up…" tool-tag rendering so the in-progress
        // state looks identical to how it renders once persisted.
        <ChatMessageBubble
          message={{
            _id: 'streaming',
            role: 'assistant',
            content: streamingContent,
            toolCalls: toolCallActive ? [{ id: 'streaming' }] : undefined,
            pending: true,
          }}
        />
      ) : (
        // No tokens or tool call yet — distinct from the "looking that up…"
        // state above (chat_bot.md §3.4).
        <div className="ss-chat-bubble-row ss-chat-bubble-row--assistant">
          <div className="ss-chat-bubble ss-chat-bubble--assistant ss-chat-bubble--typing">
            <Spinner size={14} label="Thinking" />
            <span>Thinking…</span>
          </div>
        </div>
      ))}

      {dailyLimitReached && (
        <div className="ss-chat-bubble-row ss-chat-bubble-row--assistant">
          <div className="ss-chat-bubble ss-chat-bubble--assistant">
            I'm not able to find an answer to that today — please{' '}
            <Link to="/contact">reach out to us directly</Link> and we'll get back to you.
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
